// ============================================================================
// Media helper — reads the manifest produced by scripts/optimize-images.mjs.
//
// Every image goes through here so no view ever hardcodes a path to a 7MB
// original. Emits responsive srcset plus a blurred placeholder, so a photo
// fades in over its own blur instead of popping in over an empty box.
// ============================================================================

import manifest from '../media-manifest.json';
import { html, raw, escapeHtml } from './html.js';

/** Look up an entry, e.g. media('photo/wrx-spring-wash'). */
export function media(key) {
  return manifest[key] ?? null;
}

export function mediaUrl(key, width) {
  const entry = manifest[key];
  if (!entry) return '';
  const widths = Object.keys(entry.sources).map(Number).sort((a, b) => a - b);
  const pick = width
    ? widths.find((w) => w >= width) ?? widths[widths.length - 1]
    : widths[widths.length - 1];
  return entry.sources[pick].src;
}

/**
 * A responsive <img> with blur-up.
 *
 * `sizes` should describe the layout slot, not the file — getting this right
 * is what lets a phone download the 800px file instead of the 1600px one.
 */
export function picture(key, {
  alt = '',
  sizes = '100vw',
  className = '',
  eager = false,
  ratio = null,
  // Logos are transparent PNGs. Painting a blurred placeholder behind one
  // shows a white smear through the transparency — which looked like every
  // logo sat in a gray box. Photos want it; transparent art does not.
  placeholder = true
} = {}) {
  const entry = manifest[key];
  if (!entry) {
    console.warn(`[media] missing "${key}"`);
    return raw('');
  }

  const widths = Object.keys(entry.sources).map(Number).sort((a, b) => a - b);
  const srcset = widths.map((w) => `${entry.sources[w].src} ${w}w`).join(', ');
  const fallback = entry.sources[widths[widths.length - 1]].src;

  const style = [
    placeholder ? `background-image:url('${entry.placeholder}')` : '',
    ratio ? `aspect-ratio:${ratio}` : ''
  ].filter(Boolean).join(';');

  // No inline onload handler: an `onload=""` attribute is inline script, and
  // allowing it in CSP means allowing 'unsafe-inline' for all scripts — which
  // gives up most of the protection CSP exists for. The fade-in is wired up by
  // trackImageLoads() in motion.js instead.
  return raw(`
    <img class="dw-img ${escapeHtml(className)}"
         src="${escapeHtml(fallback)}"
         srcset="${escapeHtml(srcset)}"
         sizes="${escapeHtml(sizes)}"
         alt="${escapeHtml(alt)}"
         ${eager ? 'fetchpriority="high"' : 'loading="lazy" decoding="async"'}
         ${style ? `style="${style}"` : ''}>`);
}

/** The brand logo lockup, white, for dark surfaces. */
export function brandLogo(className = '') {
  return picture('logo/dw-squad-logo', {
    alt: 'DW Squad',
    sizes: '220px',
    className: `dw-brandmark ${className}`,
    eager: true,
    placeholder: false
  });
}

/**
 * Partner brand logos.
 *
 * These arrive as a mix of dark-on-transparent and color, so the strip
 * normalizes them to grayscale and reveals full color on hover. That also
 * stops a wall of competing logo colors from fighting the page.
 */
export const PARTNER_BRANDS = [
  { key: 'logo/gtechniq-logo-2',       name: 'Gtechniq' },
  { key: 'logo/gyeon-logo-2',          name: 'Gyeon' },
  { key: 'logo/carpro-logo-2',         name: 'CarPro' },
  { key: 'logo/kochchemie-seeklogo',   name: 'Koch Chemie' },
  { key: 'logo/p-and-s-logo-2',        name: 'P&S' },
  { key: 'logo/chemical-guys-logo-2',  name: 'Chemical Guys' },
  { key: 'logo/car-candy-logo',        name: 'Car Candy' },
  { key: 'logo/tornador-logo-2',       name: 'Tornador' },
  { key: 'logo/barjack-logo-2',        name: 'Barjack' },
  { key: 'logo/hydro-logo-2',          name: 'Hydrosilex' }
];

export function brandStrip() {
  return html`
    <div class="dw-marquee" aria-label="Brands we carry">
      <div class="dw-marquee-track">
        ${[0, 1].map(() => html`
          <div class="dw-marquee-group" aria-hidden="false">
            ${PARTNER_BRANDS.map((b) => html`
              <span class="dw-brand-chip" title="${b.name}">
                ${picture(b.key, {
                  alt: b.name, sizes: '150px',
                  className: 'dw-brand-img', placeholder: false
                })}
              </span>`)}
          </div>`)}
      </div>
    </div>`;
}
